import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Building2, Loader2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import type { CompanyMembership } from "@/hooks/useCurrentCompany";

interface CompanyPickerProps {
  memberships: CompanyMembership[];
  onSelect: (companyId: string) => Promise<void>;
}

export function CompanyPicker({ memberships, onSelect }: CompanyPickerProps) {
  const { t } = useTranslation();
  const { logout } = useAuth();
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleSelect = async (id: string) => {
    setPendingId(id);
    setError(null);
    try {
      await onSelect(id);
    } catch (e) {
      setError(e instanceof Error ? e.message : t("company.switchError"));
      setPendingId(null);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6">
      <div className="w-full max-w-lg space-y-6">
        <div className="text-center space-y-2">
          <h1 className="text-2xl font-semibold">{t("company.chooseCompany")}</h1>
          <p className="text-muted-foreground">{t("company.chooseCompanyHelp")}</p>
        </div>

        <div className="space-y-3">
          {memberships.map((m) => {
            const isPending = pendingId === m.id;
            return (
              <Card
                key={m.id}
                role="button"
                tabIndex={0}
                onClick={() => !pendingId && handleSelect(m.id)}
                onKeyDown={(e) => {
                  if ((e.key === "Enter" || e.key === " ") && !pendingId) handleSelect(m.id);
                }}
                className={`p-4 flex items-center gap-3 cursor-pointer transition hover:border-primary hover:shadow-md ${
                  pendingId && !isPending ? "opacity-50 pointer-events-none" : ""
                }`}
              >
                <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                  <Building2 className="h-5 w-5" />
                </div>
                <div className="flex-1">
                  <div className="font-medium">{m.name ?? "Unnamed company"}</div>
                </div>
                {isPending && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
              </Card>
            );
          })}
        </div>

        {error && (
          <p className="text-sm text-destructive text-center" role="alert">
            {error}
          </p>
        )}

        <div className="text-center">
          <Button variant="ghost" onClick={() => logout()} disabled={!!pendingId}>
            {t("auth.signOut")}
          </Button>
        </div>
      </div>
    </div>
  );
}
